import React, { useEffect, useRef, useState } from 'react';
import { useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import {
  GetKeywordMetricsResponseRecord,
  GetKeywordMetricsResponse,
  useTopicTrafficByKeyword
} from './hooks/useTopicTrafficByKeyword';
import TopEntityChartV2, {
  TopEntityChartRef
} from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useTopicTrafficByCategory,
  GetTrafficByCategoryResponse
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTopicTrafficByCategory';
import { TrafficType } from '../types';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

/** We can't show all the bar charts at once otherwise the chart
 * will lag too much. So we limit the number of bars to show in the chart
 */
const BARS_IN_CHART = 100;

const trafficTitles = {
  totalTraffic: 'Total Traffic',
  organicTraffic: 'Organic Traffic',
  paidTraffic: 'Paid Traffic'
};

export const TrafficByKeywordChart = ({
  response,
  beaconChartWithLegendProps,
  trafficType
}: {
  response: GetKeywordMetricsResponse[];
  beaconChartWithLegendProps: Omit<BeaconChartWithLegendProps, 'children'>;
  trafficType: TrafficType;
}) => {
  const categories = response[0].records.map((record) => ({
    id: record.searchTerm,
    name: record.searchTerm,
    type: 'searchterm'
  }));

  const searchTermToMetricComparisonMap = response[1].records.reduce((acc, record) => {
    acc.set(record.searchTerm, record);
    return acc;
  }, new Map<string, GetKeywordMetricsResponseRecord>());

  return (
    <BeaconChartWithLegend
      {...beaconChartWithLegendProps}
      convertSeriesToDelimitedData={async () => {
        saveBarChartAsDelimitedData(
          response[0].records.map((record) => [record.searchTerm, record[trafficType]]),
          response[1].records.map((record) => [record.searchTerm, record[trafficType]]),
          {
            fileTitle: `${trafficTitles[trafficType]} by Keyword`,
            metricLabel: trafficTitles[trafficType]
          }
        );
      }}
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: categories
              .slice(0, BARS_IN_CHART)
              .map(({ name: searchTerm }) => [
                searchTerm,
                searchTermToMetricComparisonMap.get(searchTerm)?.[trafficType] ?? 0
              ]),
            categories,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: response[0].records.slice(0, BARS_IN_CHART).map((record) => [record.searchTerm, record[trafficType]]),
            categories,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export const TrafficByCategoryChart = ({
  response,
  groupBy,
  beaconChartWithLegendProps,
  trafficType
}: {
  response: GetTrafficByCategoryResponse[];
  groupBy: 'categoryId' | 'subcategoryId';
  beaconChartWithLegendProps: Omit<BeaconChartWithLegendProps, 'children'>;
  trafficType: TrafficType;
}) => {
  const chartRef = useRef<TopEntityChartRef>(null);
  const allCategories = useAppSelector((state) =>
    groupBy === 'categoryId' ? state.categoriesByRetailerId[0] : state.subCategories
  );

  // Reset the chart to the beginning when the groupBy changes
  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    chartRef.current?.api.setRangeStart(0);
  }, [groupBy]);

  const highchartsCategories = response[0].records
    .map((record) => ({
      id: record?.[groupBy]?.toString(),
      name:
        allCategories.find((category) => category.id.toString() === record?.[groupBy].toString())?.name ??
        record?.[groupBy].toString(),
      type: groupBy === 'categoryId' ? 'category' : 'subcategory'
    }))
    .filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id));

  const categoryToMetricMap = response[0].records.reduce((acc, record) => {
    const categoryId = record?.[groupBy]?.toString();
    if (!acc.has(categoryId)) {
      acc.set(categoryId, 0);
    }

    acc.set(categoryId, acc.get(categoryId) + record[trafficType]);
    return acc;
  }, new Map<string, number>());

  const categoryToMetricComparisonMap = response[1].records.reduce((acc, record) => {
    const categoryId = record?.[groupBy]?.toString();
    if (!acc.has(categoryId)) {
      acc.set(categoryId, 0);
    }

    acc.set(categoryId, acc.get(categoryId) + record[trafficType]);
    return acc;
  }, new Map<string, number>());

  const getMainData = (endIndex?: number): [string, number][] => {
    return highchartsCategories
      .slice(0, endIndex)
      .map(({ id: categoryId, name }) => [name, categoryToMetricMap.get(categoryId) ?? 0] as [string, number])
      .sort((a, b) => b[1] - a[1]);
  };
  const getComparisonData = (endIndex?: number): [string, number][] => {
    return highchartsCategories
      .slice(0, endIndex)
      .map(({ id: categoryId, name }) => [name, categoryToMetricComparisonMap.get(categoryId) ?? 0]);
  };

  return (
    <BeaconChartWithLegend
      {...beaconChartWithLegendProps}
      convertSeriesToDelimitedData={async () =>
        saveBarChartAsDelimitedData(getMainData(), getComparisonData(), {
          fileTitle: `${trafficTitles[trafficType]} by ${groupBy === 'categoryId' ? 'Category' : 'Subcategory'}`,
          metricLabel: trafficTitles[trafficType]
        })
      }
    >
      <TopEntityChartV2
        ref={chartRef}
        chartSeries={[
          {
            type: 'column',
            data: getComparisonData(BARS_IN_CHART),
            categories: highchartsCategories,
            metricType: METRICTYPE.VOLUME
          },
          {
            type: 'column',
            data: getMainData(BARS_IN_CHART),
            categories: highchartsCategories,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export interface TrafficBarChartForTopicsProps {
  trafficType: TrafficType;
}

export const TrafficBarChartForTopics = ({ trafficType }: TrafficBarChartForTopicsProps) => {
  const companyName = useCompanyName();
  const { displayName: mainPeriodDisplay } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplay } = useAppSelector((state) => state.comparisonTimePeriod);
  const [selectedDropdownId, setSelectedDropdownId] = useState<'keyword' | 'category' | 'subcategory'>('keyword');

  const { response: keywordsResponse, isLoading } = useTopicTrafficByKeyword({
    sortField: trafficType,
    includeClassification: false
  });
  const { response: categoriesResponse } = useTopicTrafficByCategory({ sortField: trafficType });
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = () => {
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${trafficTitles[trafficType].toLowerCase()} for ${companyName}`}
          {taxonomyText}
          {` in ${entityPhrase} in each ${selectedDropdownId}`}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  const beaconChartWithLegendProps = {
    title: trafficTitles[trafficType],
    loading: isLoading,
    loadingComponent: <SplineChartLoading />,
    includeSummaryInfo: false,
    subtitle: getSubtitle(),
    primaryLegendProps: {
      displayName: mainPeriodDisplay
    },
    comparisonLegendProps: {
      displayName: comparisonPeriodDisplay
    },
    singleDropdownProps: {
      prefix: `${trafficTitles[trafficType]} by `,
      selectedId: selectedDropdownId,
      onChange: (option) => setSelectedDropdownId(option.id as 'keyword' | 'category' | 'subcategory'),
      options: [
        {
          id: 'keyword',
          label: 'Keyword'
        },
        {
          id: 'category',
          label: 'Category'
        },
        {
          id: 'subcategory',
          label: 'Subcategory'
        }
      ]
    }
  };

  return selectedDropdownId === 'keyword' ? (
    <TrafficByKeywordChart
      response={keywordsResponse}
      beaconChartWithLegendProps={beaconChartWithLegendProps}
      trafficType={trafficType}
    />
  ) : (
    <TrafficByCategoryChart
      response={categoriesResponse}
      groupBy={selectedDropdownId === 'category' ? 'categoryId' : 'subcategoryId'}
      beaconChartWithLegendProps={beaconChartWithLegendProps}
      trafficType={trafficType}
    />
  );
};
